import { useEffect, useMemo, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qAppConfig,
  qAppointmentProviders,
  qPatientById,
  qPrescriptionPrintSettings,
  qPrescriptionsByEncounter,
  qUserProviderLink,
  type AppConfigRow,
  type AppointmentProviderRow,
  type PatientDetailRow,
  type PatientPrescriptionRow,
  type PrescriptionPrintSettingsRow,
  type UserProviderLinkRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import {
  PrescriptionPrintLayout,
  type PrintPrescriptionItem,
} from '../clinic/prescription-print/PrescriptionPrintLayout';
import {
  formatPatientAge,
  resolveDoctor,
  resolvePrintSettings,
} from '../clinic/prescription-print/print-utils';
import { useBrandTokens } from '../../shell/shared';

// PRD.202 — drop-in mount that lets Pharmacy contexts print the existing
// encounter prescription document directly (no navigation hop).
//
// Implementation strategy: render the prescription layout to static HTML
// via `react-dom/server`, push that HTML into a hidden iframe, then call
// `iframe.contentWindow.print()`. The iframe is fully isolated from the
// host page — its own @page rule, its own visibility — so the host
// page's tab state, scroll position, and CSS context cannot be affected
// by the print in any way (this is the cause of the earlier "goes to
// prescription tab" symptom: the browser's print snapshot was capturing
// the host page rather than a clean isolated surface).
//
// No DB writes happen here — pure read + browser print.

export interface PrescriptionPrintRequest {
  patientId: string;
  encounterId: string;
}

export function PrescriptionPrintMount({
  request,
  onAfterPrint,
}: {
  request: PrescriptionPrintRequest | null;
  onAfterPrint: () => void;
}) {
  const { user, claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';

  const patientId = request?.patientId ?? '';
  const encounterId = request?.encounterId ?? '';

  const { data: rxRows = [] } = usePowerSyncQuery<PatientPrescriptionRow>(
    qPrescriptionsByEncounter(encounterId),
  );
  const { data: printSettingsRows = [] } = usePowerSyncQuery<PrescriptionPrintSettingsRow>(
    qPrescriptionPrintSettings(),
  );
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const { data: providerLinkRows = [] } = usePowerSyncQuery<UserProviderLinkRow>(
    qUserProviderLink(orgId, userId),
  );
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(qPatientById(patientId));

  const printSettings = useMemo(
    () => resolvePrintSettings(printSettingsRows[0] ?? null, appConfigRows[0] ?? null),
    [printSettingsRows, appConfigRows],
  );
  const printDoctor = useMemo(() => {
    const linkedProviderId = providerLinkRows[0]?.provider_id ?? null;
    const linkedProvider = linkedProviderId
      ? (providerRows.find((p) => p.id === linkedProviderId) ?? null)
      : null;
    return resolveDoctor(linkedProvider, user?.displayName ?? null);
  }, [providerLinkRows, providerRows, user?.displayName]);
  const printPatient = useMemo(() => {
    const p = patientRows[0];
    return {
      name: p?.full_name ?? '',
      age: formatPatientAge(p?.birth_date),
      sex: p?.sex ?? '',
    };
  }, [patientRows]);
  const printItems: PrintPrescriptionItem[] = useMemo(
    () =>
      rxRows.map((r) => {
        const snapshotDetails = [
          r.brand_name_snapshot,
          r.generic_name_snapshot,
          r.strength_snapshot,
          r.dosage_form_snapshot,
        ]
          .filter(Boolean)
          .join(' · ');
        return {
          itemName: r.item_name_snapshot ?? r.free_text_name ?? r.item_name ?? 'Untitled medicine',
          itemDetails: r.item_details ?? snapshotDetails,
          intakeTiming: r.intake_timing ?? '',
          doseQuantity: r.dose_quantity ?? r.default_dosage_snapshot ?? '',
          scheduleMorning: r.schedule_morning === 1,
          scheduleAfternoon: r.schedule_afternoon === 1,
          scheduleNight: r.schedule_night === 1,
          durationValue: r.duration_value ?? null,
          durationUnit: r.duration_unit ?? '',
          instructions: r.instructions ?? r.default_instruction_snapshot ?? '',
        };
      }),
    [rxRows],
  );

  const lastFiredKey = useRef<string | null>(null);

  // Wait until ALL the data we need to render the prescription is loaded.
  // PowerSync queries each resolve independently — without this gate, the
  // print can fire after `printItems` arrives but before the patient row
  // does, producing a printout with blank Patient Name / Age cells.
  const patientLoaded = patientRows.length > 0;
  const itemsLoaded = printItems.length > 0;

  useEffect(() => {
    if (!request) {
      // Reset so the same encounter can print again on the next click.
      lastFiredKey.current = null;
      return;
    }
    const key = `${request.patientId}:${request.encounterId}`;
    if (lastFiredKey.current === key) return;

    if (!patientLoaded || !itemsLoaded) {
      // Wait briefly for PowerSync to settle, then warn if still empty.
      // Items missing → no prescriptions to print. Patient missing →
      // unusual; we don't want to print without a name on the label.
      const t = setTimeout(() => {
        if (!itemsLoaded) {
          window.alert(
            'No prescription items found for this encounter. Open the encounter Prescriptions tab to add items first.',
          );
          onAfterPrint();
        } else if (!patientLoaded) {
          window.alert('Patient details are still loading. Please try Print Rx again in a moment.');
          onAfterPrint();
        }
      }, 1200);
      return () => clearTimeout(t);
    }

    lastFiredKey.current = key;

    const html = renderToStaticMarkup(
      <PrescriptionPrintLayout
        settings={printSettings}
        doctor={printDoctor}
        patient={printPatient}
        date={new Date().toISOString()}
        items={printItems}
        accentColor={primary}
      />,
    );

    // Iframe-based print so the host page is not affected at all.
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.opacity = '0';
    document.body.appendChild(iframe);

    const cleanup = () => {
      // Defer the iframe removal so the print subsystem in some browsers
      // can finish closing the dialog before the document goes away.
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 200);
      onAfterPrint();
    };

    const doc = iframe.contentDocument;
    if (!doc) {
      cleanup();
      return;
    }
    doc.open();
    doc.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Prescription</title>
<style>
  html, body { margin: 0; padding: 0; background: #ffffff; }
  @page { size: A4; margin: 0; }
  body { font-family: system-ui, -apple-system, "Segoe UI", sans-serif; }
  body > * { width: 210mm; min-height: 297mm; box-sizing: border-box; }
</style>
</head>
<body>${html}</body>
</html>`);
    doc.close();

    const onLoad = () => {
      const w = iframe.contentWindow;
      if (!w) {
        cleanup();
        return;
      }
      // afterprint is the source of truth for "print dialog dismissed".
      // Bind on the iframe's window so the host page is unaffected.
      const onAfter = () => {
        w.removeEventListener('afterprint', onAfter);
        cleanup();
      };
      w.addEventListener('afterprint', onAfter);
      try {
        w.focus();
        w.print();
      } catch {
        cleanup();
      }
      // Watchdog in case afterprint doesn't fire (older browsers).
      setTimeout(() => {
        try {
          w.removeEventListener('afterprint', onAfter);
        } catch {
          // ignore
        }
        cleanup();
      }, 60000);
    };

    // The iframe needs a tick to actually load its document content.
    if (iframe.contentDocument?.readyState === 'complete') {
      onLoad();
    } else {
      iframe.addEventListener('load', onLoad, { once: true });
      // Fallback: most browsers populate the iframe synchronously after
      // doc.write/doc.close, so trigger directly on the next tick.
      setTimeout(onLoad, 0);
    }
  }, [
    request,
    patientLoaded,
    itemsLoaded,
    printItems,
    printSettings,
    printDoctor,
    printPatient,
    primary,
    onAfterPrint,
  ]);

  // No on-screen DOM at all — everything happens via the iframe.
  return null;
}
